// src/layout/Dashboard/Header/HeaderContent/Profile/index.tsx

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

import Avatar from 'components/@extended/Avatar';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';

import { LogOut, Settings } from 'iconoir-react';

// Account Settings Dialog
import AccountSettingsDialog from 'components/account/AccountSettingsDialog';


export default function Profile() {
  const navigate = useNavigate();

  const { logout, user } = useAuth();

  // Popper state
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  // Account Settings Dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true, state: { from: '' } });
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenSettings = () => {
    setOpen(false); // Close the dropdown
    setSettingsOpen(true); // Open the dialog
  };

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            bgcolor: open ? 'grey.100' : 'transparent',
            borderRadius: 1,
            '&:hover': { bgcolor: 'secondary.lighter' },
            '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
            ...theme.applyStyles('dark', {
              bgcolor: open ? 'background.default' : 'transparent',
              '&:hover': { bgcolor: 'secondary.light' }
            })
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
            <Avatar alt="profile user" sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'common.white' }}>
              <PersonRoundedIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle1" sx={{ textTransform: 'none' }}>
              {user?.name?.toLowerCase()}
            </Typography>
          </Stack>
        </ButtonBase>

        <Popper
          placement="bottom-end"
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
        >
          {({ TransitionProps }) => (
            <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
              <Paper
                sx={(theme) => ({
                  boxShadow: theme.customShadows.z1,
                  width: 290,
                  minWidth: 240,
                  maxWidth: { xs: 250, md: 290 }
                })}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard elevation={0} border={false} content={false}>
                    <CardContent sx={{ px: 2.5, pt: 3 }}>
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid>
                          <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                            <Avatar alt="profile user" sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'common.white' }}>
                              <PersonRoundedIcon fontSize="small" />
                            </Avatar>
                            <Stack>
                              <Typography variant="h6" sx={{ textTransform: 'none' }}>
                                {user?.name?.toLowerCase()}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>

                    <Divider />
                    
                    <List dense disablePadding>
                      {/* Account Settings */}
                      <ListItemButton onClick={handleOpenSettings}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Settings style={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText primary="Account Settings" />
                      </ListItemButton>

                      {/* Logout */}
                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          handleLogout();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LogOut style={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                      </ListItemButton>
                    </List>
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      </Box>

      {/* Account Settings Dialog */}
      <AccountSettingsDialog 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)} 
      />
    </>
  );
}
